import classNames from "classnames";
import React from "react";

export const badgeStyles = [
  "info",
  "default",
  "error",
  "error-dark",
  "warning",
  "success",
  "secondary",
  "primary-alt",
] as const;

type BadgeStyle = (typeof badgeStyles)[number];

type Props<T extends React.ElementType = "span" | "button"> = {
  as?: T;
  text: string;
  textStyle?: "light" | "dark";
  badgeStyle?: BadgeStyle;
  size?: "normal" | "small";
  textSize?: "xs" | "sm" | "md";
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  spacing?: "tight-hug";
  onRightIconClick?(): void;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

export const Badge = ({
  as = "span",
  text,
  badgeStyle = "default",
  size = "normal",
  leftIcon,
  rightIcon,
  onRightIconClick,
  textStyle = "light",
  textSize = "md",
  onClick,
  disabled = false,
  spacing,
}: Props) => {
  const iconClassName = classNames(
    "rounded-3xl",
    { "text-lg": size === "normal" },
    { "text-base": size === "small" },
    {
      "bg-primary-500 group-hover:bg-primary-700 group-active:bg-primary-300 group-active:text-primary-500":
        badgeStyle === "info",
      "bg-gray group-hover:bg-gray-700 group-active:bg-gray-300 group-active:text-gray":
        badgeStyle === "default",
      "bg-secondary-salmon group-hover:bg-secondary-salmon-700 group-active:bg-secondary-salmon-300 group-active:text-secondary-salmon":
        badgeStyle === "secondary",
      "bg-error group-hover:bg-error-700 group-active:bg-error-300 group-active:text-error":
        badgeStyle === "error",
      "bg-warning group-hover:bg-warning-700 group-active:bg-warning-300 group-active:text-warning":
        badgeStyle === "warning",
      "bg-tertiary-apple-600 group-hover:bg-tertiary-apple-800 group-active:bg-tertiary-apple-400 group-active:text-tertiary-apple-600":
        badgeStyle === "success",
      "bg-primary-light-800 group-hover:bg-primary-light-900 group-active:bg-primary-light group-active:text-primary-light-700":
        badgeStyle === "primary-alt",
    }
  );

  const Component = as;

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
      return;
    }
    if (onRightIconClick) {
      onRightIconClick();
    }
  };

  return (
    <Component
      type={as === "button" ? "button" : undefined}
      onClick={handleOnClick}
      className={classNames(
        "inline-flex items-center space-x-1 rounded-full text-text-dark-100 focus:outline-none focus-visible:shadow-elevation-01",
        {
          group: as === "button",
          "text-sm font-semibold px-2 py-1": size === "normal",
          "text-xs font-semibold px-1.5 py-0.5": size === "small",
          "bg-primary-500": badgeStyle === "info",
          "hover:bg-primary-bold-300 active:bg-primary-500":
            as === "button" && badgeStyle === "info",
          "bg-gray-600": badgeStyle === "default",
          "hover:bg-gray-300 active:bg-gray":
            as === "button" && badgeStyle === "default",
          "bg-secondary-salmon-100": badgeStyle === "secondary",
          "hover:bg-secondary-salmon-300 active:bg-secondary-salmon":
            as === "button" && badgeStyle === "secondary",
          "bg-error-100": badgeStyle === "error",
          "bg-secondary-salmon": badgeStyle === "error-dark",
          "hover:bg-error-300 active:bg-error":
            as === "button" && badgeStyle === "error",
          "bg-warning": badgeStyle === "warning",
          "hover:bg-warning-300 active:bg-warning":
            as === "button" && badgeStyle === "warning",
          "bg-tertiary-apple-200": badgeStyle === "success",
          "hover:bg-tertiary-apple-400 active:bg-tertiary-apple-600":
            as === "button" && badgeStyle === "success",
          "bg-primary-light": badgeStyle === "primary-alt",
          "hover:bg-primary-light-600 active:bg-primary-light-900":
            as === "button" && badgeStyle === "primary-alt",
        }
      )}
    >
      {!disabled && leftIcon && (
        <span className={iconClassName}>{leftIcon}</span>
      )}
      <span
        className={classNames("whitespace-nowrap text-center", {
          "text-text-dark-100": textStyle === "light",
          "text-gray-900": textStyle === "dark",
          "text-md font-semibold": textSize === "md",
          "text-sm font-semibold": textSize === "sm",
          "text-xs font-normal": textSize === "xs",
          "px-2": spacing === "tight-hug",
          "px-2 py-1": !spacing,
        })}
      >
        {text}
      </span>
      {!disabled && rightIcon && (
        <div className={iconClassName}>{rightIcon}</div>
      )}
    </Component>
  );
};
